import {
  ALLOWED_BODY,
  DEFAULT_MESSAGE_CODE,
  DEFAULT_REFRESH_TOKEN_ERROR,
  ROUTES_TO_EXCLUDE_FROM_REFRESH,
} from './constants';
import { getCookie as defaultGetCookie } from './getCookie';
import type { ApiError } from './types';
import type {
  ApiClient,
  ApiClientConfig,
  ApiCommunicator,
  ApiResponse,
  RequestParams,
} from './types';

export function createApiClient(config: ApiClientConfig): ApiClient {
  const {
    baseUrl,
    getAccessToken,
    onAuthFailure,
    appTypeHeader,
    getDefaultErrorMessage = () => 'Unexpected error',
    getCookie: readCookie = defaultGetCookie,
    publicUrl = '',
    refreshPath = '/auth/refresh',
    refreshTokenErrorMessage = DEFAULT_REFRESH_TOKEN_ERROR,
  } = config;

  let isRefreshing = false;
  let failedRequestsQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
  }> = [];

  const processQueue = (error: Error | null) => {
    failedRequestsQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(true);
      }
    });
    failedRequestsQueue = [];
  };

  function formatFieldErrors(data: ApiResponse['data']): string {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return '';
    }
    return Object.values(data)
      .filter((v) => typeof v === 'string' && v.length > 0)
      .join('\n');
  }

  function createError(error: ApiResponse): ApiError {
    const INTERNAL_ERROR = 500;
    const body = error ?? ({} as ApiResponse);
    const responseMessage = body.responseMessage;

    // Backend error payloads use responseMessage + optional field errors in data.
    if (responseMessage || body.data) {
      const mainMessage =
        responseMessage?.message || body.message || getDefaultErrorMessage();
      const fieldMessages = formatFieldErrors(body.data);

      let message = mainMessage;
      if (fieldMessages) {
        message =
          mainMessage && mainMessage !== getDefaultErrorMessage()
            ? `${mainMessage}\n${fieldMessages.replace(/<br>/g, '\n')}`
            : fieldMessages.replace(/<br>/g, '\n');
      }

      const status =
        typeof body.status === 'number'
          ? body.status
          : responseMessage?.status ?? INTERNAL_ERROR;

      return {
        ...body,
        message,
        messageCode: body.messageCode ?? DEFAULT_MESSAGE_CODE,
        status,
        responseMessage,
        data: body.data,
      } as ApiError;
    }

    const secureMessage = body.message || getDefaultErrorMessage();

    return {
      message: secureMessage,
      messageCode: body.messageCode ?? DEFAULT_MESSAGE_CODE,
      status: body.status ?? INTERNAL_ERROR,
    } as ApiError;
  }

  async function handleTokenRefreshAndRetry<T>(
    originalRequestFn: () => Promise<T>,
  ): Promise<T> {
    isRefreshing = true;
    try {
      const refreshUrl = `${baseUrl}${refreshPath}`;

      const refreshResponse = await fetch(refreshUrl, {
        method: 'POST',
        credentials: 'include',
      });

      if (!refreshResponse.ok) {
        throw new Error(refreshTokenErrorMessage);
      }

      processQueue(null);
      return await originalRequestFn();
    } catch (refreshError) {
      processQueue(refreshError as Error);
      onAuthFailure();
      throw refreshError;
    } finally {
      isRefreshing = false;
    }
  }

  function serve(method: string, isExternalApi: boolean): ApiCommunicator {
    return async function sendHttpRequest<T>(
      path: string,
      params?: RequestParams,
    ): Promise<T> {
      params = {
        auth: true,
        ...params,
      };

      const performRequest = async (): Promise<T> => {
        try {
          const body =
            ALLOWED_BODY.includes(method) && params.body
              ? params.formData
                ? params.body
                : JSON.stringify(params.body)
              : undefined;

          const query = params.query
            ? Object.entries(params.query)
                .filter(([_, v]) => v !== undefined && v !== null && v !== '')
                .map(
                  ([k, v]) =>
                    `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
                )
                .join('&')
            : undefined;

          const apiBaseUrl = isExternalApi ? '' : params.baseUrl ?? baseUrl;
          const url = query ? `${path}?${query}` : path;
          const totalUrl = apiBaseUrl + url;

          const accessToken = getAccessToken();
          const auth =
            params.auth && accessToken ? `Bearer ${accessToken}` : undefined;

          let headers: HeadersInit = {
            Accept: 'application/json',
          };

          if (appTypeHeader) {
            headers['X-App-Type'] = appTypeHeader;
          }

          const xsrfToken = readCookie('XSRF-TOKEN');

          if (xsrfToken && method !== 'GET') {
            headers['X-XSRF-TOKEN'] = xsrfToken;
          }

          if (!params.formData) {
            headers['Content-Type'] = 'application/json';
          }

          if (auth && !isExternalApi) {
            headers['Authorization'] = auth;
          }

          if (isExternalApi) {
            headers['Accept'] = '*/*';
            headers['Access-Control-Allow-Origin'] = `${publicUrl}/, *`;
            headers['Access-Control-Allow-Headers'] = '*';
            headers['Access-Control-Allow-Methods'] = '*';
          }

          if (params.headers) {
            headers = {
              ...headers,
              ...params.headers,
            };
          }

          const request: RequestInit = {
            method,
            body: body as BodyInit | null | undefined,
            headers,
            credentials: isExternalApi ? 'omit' : 'include',
          };

          const response = await fetch(totalUrl, request);

          if (!response.ok) {
            const errorBody = await (response.json() as Promise<ApiResponse>);
            const status = response.status;
            errorBody.status = status;

            const isAuthRoute = ROUTES_TO_EXCLUDE_FROM_REFRESH.some((_url) =>
              path?.endsWith(window.location.pathname),
            );

            if (status === 401 && !isAuthRoute) {
              if (isRefreshing) {
                return new Promise((resolve, reject) => {
                  failedRequestsQueue.push({ resolve, reject });
                }).then(() => {
                  return performRequest();
                });
              }

              return handleTokenRefreshAndRetry(performRequest);
            } else {
              throw errorBody;
            }
          }

          if (params.returnRawResponse) {
            return response as unknown as T;
          }

          const jsonRaw = await response.text();

          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
          let json: any;

          try {
            json = JSON.parse(jsonRaw);
          } catch {
            json = jsonRaw;
          }

          return json as T;
        } catch (error) {
          throw createError(error as ApiResponse);
        }
      };

      return performRequest();
    };
  }

  return {
    get: serve('GET', false),
    post: serve('POST', false),
    put: serve('PUT', false),
    patch: serve('PATCH', false),
    delete: serve('DELETE', false),
    getExternalAPI: serve('GET', true),
  };
}
